import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { homeTranslations } from '../translations/home';
import { useLocation, useNavigate } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';

/**
 * Header Component
 * 
 * Main navigation header with adaptive navigation based on current page.
 * - On homepage: Shows full navigation menu
 * - On landing pages: Shows simplified navigation (Home, Current Page, Contact)
 * 
 * Features:
 * - Language switcher (EN/ES)
 * - Responsive mobile menu
 * - Smart scroll-to-section for anchor links
 * - Active page highlighting
 */
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language } = useLanguage();
  const t = homeTranslations[language].nav;
  const location = useLocation();
  const navigate = useNavigate();

  // Detectar si estamos en una landing page
  const isLandingPage = location.pathname !== '/';
  const currentPage = location.pathname.replace('/', '');
  
  // Obtener el nombre de la página actual
  const getPageName = () => {
    switch(currentPage) {
      case 'cnc': return t.cnc;
      case 'harness': return t.harness;
      case 'automation': return t.automation;
      default: return '';
    }
  };

  // Navegación simplificada para landing pages
  const landingNavigation = [
    { name: t.home, href: '/' },
    { name: getPageName(), href: location.pathname },
    { name: t.contact, href: '#cta' }
  ];

  // Navegación completa para home
  const homeNavigation = [
    { name: t.home, href: '/' },
    { name: t.services, href: '#services' },
    { name: t.cnc, href: '/cnc' },
    { name: t.harness, href: '/harness' },
    { name: t.automation, href: '/automation' },
    { name: t.contact, href: '#contact' }
  ];

  const navigation = isLandingPage ? landingNavigation : homeNavigation;

  const handleNavClick = (href) => {
    if (href.startsWith('/')) {
      navigate(href);
    } else {
      // Para enlaces de anclaje (#contact, #services, etc.)
      const elementId = href.replace('#', '');
      
      // Si estamos en una landing page y queremos ir a contact, usar 'cta'
      const targetId = (!isLandingPage && elementId === 'cta') ? 'contact' : elementId;
      const element = document.getElementById(targetId);
      
      if (element) {
        // Si el elemento existe en la página actual, hacer scroll
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        // Si no existe, navegar a home con el hash
        navigate('/' + href);
      }
    }
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-gray-800">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <a href="/" className="flex items-center space-x-3">
              <img 
                src="/images/logo/dsi-logo.png" 
                alt="DSI Durango Logo" 
                className="sm:h-14 md:h-16 w-auto object-contain shrink-0 pr-2"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => {
                  if (!item.href.startsWith('http')) {
                    e.preventDefault();
                    handleNavClick(item.href);
                  }
                }}
                className={`text-gray-300 hover:text-[#B91C1C]  text-sm font-medium ${
                  location.pathname === item.href ? 'text-[#B91C1C]' : ''
                }`}
              >
                {item.name}
              </a>
            ))}
            <LanguageSwitcher />
            <Button 
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white "
              onClick={() => handleNavClick('#cta')}
            >
              {t.getQuote}
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-3">
            <LanguageSwitcher />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-300 hover:text-white p-2"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2 border-t border-gray-800">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className={`block px-4 py-2 text-gray-300 hover:text-[#B91C1C] hover:bg-gray-900  ${
                  location.pathname === item.href ? 'text-[#B91C1C] bg-gray-900' : ''
                }`}
                onClick={(e) => {
                  if (!item.href.startsWith('http')) {
                    e.preventDefault();
                    handleNavClick(item.href);
                  }
                }}
              >
                {item.name}
              </a>
            ))}
            <div className="px-4 pt-2">
              <Button 
                className="w-full bg-[#B91C1C] hover:bg-[#991B1B] text-white"
                onClick={() => handleNavClick('#cta')}
              >
                {t.getQuote}
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
